/**
 * UploadPreviewSection - Reusable component for displaying file upload previews
 * Shows upload status, metadata, and action buttons for review
 */

import React, { useState } from 'react';
import DataPreviewModal from '../DataPreviewModal';
import PDFPreviewModal from '../PDFPreviewModal';

interface Upload {
  upload_id: string;
  filename: string;
  file_size: number;
  file_type: string;
  template_type: string;
  upload_status: string;
  total_rows: number;
  created_at: string;
  error_message?: string;
}

interface UploadPreviewSectionProps {
  uploads: Upload[];
}

const UploadPreviewSection: React.FC<UploadPreviewSectionProps> = ({ uploads }) => {
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [showDataPreviewModal, setShowDataPreviewModal] = useState(false);
  const [showPDFPreviewModal, setShowPDFPreviewModal] = useState(false);
  const [selectedPDFUploadId, setSelectedPDFUploadId] = useState<string | null>(null);

  // Helper functions
  const getFileTypeFromFilename = (filename: string): string => {
    const extension = filename.toLowerCase().split('.').pop();
    switch (extension) {
      case 'pdf':
        return 'PDF';
      case 'xlsx':
      case 'xls':
      case 'csv':
        return 'Excel';
      case 'doc':
      case 'docx':
        return 'Word';
      case 'ppt':
      case 'pptx':
        return 'PowerPoint';
      default:
        return 'file';
    }
  };

  const isPDFFile = (filename: string): boolean => {
    return filename.toLowerCase().endsWith('.pdf');
  };

  if (uploads.length === 0) {
    return null;
  }

  return (
    <>
      <div className="space-y-6 mb-8">
        {uploads.map((upload) => (
          <div key={upload.upload_id} className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-lg font-medium text-kielis-navy mb-2 flex items-center">
                  {upload.upload_status === 'ready' && '📊'}
                  {upload.upload_status === 'ingesting' && '⏳'}
                  {upload.upload_status === 'complete' && '✅'}
                  {' '}
                  {upload.filename} 
                  {upload.upload_status === 'ready' && ' Ready for Review'}
                  {upload.upload_status === 'ingesting' && ' Importing...'}
                  {upload.upload_status === 'complete' && ' Import Complete'}
                </h4>
                <div className="space-y-2 text-sm text-gray-600">
                  <p>
                    📁 <span className="font-medium">{upload.total_rows || 'Unknown'} rows</span> processed from {getFileTypeFromFilename(upload.filename)} upload
                  </p>
                  <p>
                    📋 <span className="font-medium">{upload.template_type}</span> template detected
                  </p>
                  <p>
                    📅 <span className="font-medium">Uploaded {new Date(upload.created_at).toLocaleDateString()}</span>
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                {upload.upload_status === 'ready' && (
                  <>
                    {isPDFFile(upload.filename) ? (
                      <>
                        <button
                          onClick={() => {
                            setSelectedPDFUploadId(upload.upload_id);
                            setShowPDFPreviewModal(true);
                          }}
                          className="bg-kielis-navy text-white px-6 py-3 rounded-lg hover:bg-opacity-90 transition-colors"
                        >
                          📄 Review Document
                        </button>
                        <p className="text-xs text-gray-500 text-center">
                          Review extracted content before import
                        </p>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => {
                            setUploadId(upload.upload_id);
                            setShowDataPreviewModal(true);
                          }}
                          className="bg-kielis-navy text-white px-6 py-3 rounded-lg hover:bg-opacity-90 transition-colors"
                        >
                          Review & Fix Data
                        </button>
                        <p className="text-xs text-gray-500 text-center">
                          Check field mapping & data quality
                        </p>
                      </>
                    )}
                  </>
                )}
                {upload.upload_status === 'ingesting' && (
                  <>
                    <div className="bg-blue-100 text-blue-800 px-6 py-3 rounded-lg text-center flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      Importing Data...
                    </div>
                    <p className="text-xs text-gray-500 text-center">
                      Processing in background
                    </p>
                  </>
                )}
                {upload.upload_status === 'complete' && (
                  <>
                    <div className="bg-green-100 text-green-800 px-6 py-3 rounded-lg text-center">
                      ✅ Import Successful
                    </div>
                    <p className="text-xs text-gray-500 text-center">
                      Data imported successfully
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Quick stats */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div className="text-center">
                  <div className="font-medium text-kielis-navy">{upload.total_rows || 0}</div>
                  <div className="text-gray-600">Total Rows</div>
                </div>
                <div className="text-center">
                  <div className="font-medium text-kielis-navy">
                    {upload.template_type || 'Unknown'}
                  </div>
                  <div className="text-gray-600">Template Type</div>
                </div>
                <div className="text-center">
                  <div className={`font-medium ${
                    upload.upload_status === 'ready' ? 'text-green-600' : 
                    upload.upload_status === 'ingesting' ? 'text-blue-600' :
                    upload.upload_status === 'complete' ? 'text-green-600' : 'text-gray-600'
                  }`}>
                    {upload.upload_status === 'ready' ? 'Ready' : 
                     upload.upload_status === 'ingesting' ? 'Importing' :
                     upload.upload_status === 'complete' ? 'Complete' : 
                     upload.upload_status}
                  </div>
                  <div className="text-gray-600">Status</div>
                </div>
                <div className="text-center">
                  <div className="font-medium text-blue-600">
                    {(upload.file_size / (1024 * 1024)).toFixed(1)}MB
                  </div>
                  <div className="text-gray-600">File Size</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Data Preview Modal */}
      {uploadId && (
        <DataPreviewModal
          uploadId={uploadId}
          isOpen={showDataPreviewModal}
          onClose={() => setShowDataPreviewModal(false)}
          onConfirm={async () => {
            setShowDataPreviewModal(false);
            // The parent component should handle the actual status update
          }}
        />
      )}

      {/* PDF Preview Modal */}
      {selectedPDFUploadId && (
        <PDFPreviewModal
          uploadId={selectedPDFUploadId}
          isOpen={showPDFPreviewModal}
          onClose={() => {
            setShowPDFPreviewModal(false);
            setSelectedPDFUploadId(null);
          }}
          onApprove={() => {
            setShowPDFPreviewModal(false);
            setSelectedPDFUploadId(null);
            // The parent component should handle the actual status update
          }}
        />
      )}
    </>
  );
};

export default UploadPreviewSection;